/* eslint-disable no-prototype-builtins */
import { type ClassValue, clsx } from "clsx";
import qs from "query-string";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { BranchRole, User } from "../types";
import { BUSINESS_TIME_ZONE, businessToday, businessYear } from "./date";
import { CalendarData, GroupedLesson, LessonData } from "@/types/calendar";
import type { EventInput } from "@fullcalendar/core/index.js";
import {
  IsSuperadmin,
  IsMasterAdminOrHigher,
  IsPrincipalOrHigher,
  IsManagerOrHigher,
} from "@/constants/index";
import {
  SUPERADMIN,
  MASTERADMIN,
  PARENT,
  TEACHER,
  PRINCIPAL,
  MANAGER,
} from "@/constants/message";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// FORMAT DATE TIME
// Every option set pins timeZone. Without it the server formats in the VPS
// zone and the browser in the viewer's, so the two renders disagree — React
// hydration errors #425/#422.
export const formatDateTime = (dateString: Date) => {
  const dateTimeOptions: Intl.DateTimeFormatOptions = {
    weekday: "short", // abbreviated weekday name (e.g., 'Mon')
    month: "short", // abbreviated month name (e.g., 'Oct')
    day: "numeric", // numeric day of the month (e.g., '25')
    hour: "numeric", // numeric hour (e.g., '8')
    minute: "numeric", // numeric minute (e.g., '30')
    hour12: true, // use 12-hour clock (true) or 24-hour clock (false)
    timeZone: BUSINESS_TIME_ZONE,
  };

  const dateDayOptions: Intl.DateTimeFormatOptions = {
    weekday: "short", // abbreviated weekday name (e.g., 'Mon')
    year: "numeric", // numeric year (e.g., '2023')
    month: "2-digit", // abbreviated month name (e.g., 'Oct')
    day: "2-digit", // numeric day of the month (e.g., '25')
    timeZone: BUSINESS_TIME_ZONE,
  };

  const dayLongOptions: Intl.DateTimeFormatOptions = {
    weekday: "long",
    timeZone: BUSINESS_TIME_ZONE,
  };

  const dateOptions: Intl.DateTimeFormatOptions = {
    month: "short", // abbreviated month name (e.g., 'Oct')
    year: "numeric", // numeric year (e.g., '2023')
    day: "numeric", // numeric day of the month (e.g., '25')
    timeZone: BUSINESS_TIME_ZONE,
  };

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric", // numeric hour (e.g., '8')
    minute: "numeric", // numeric minute (e.g., '30')
    hour12: true, // use 12-hour clock (true) or 24-hour clock (false)
    timeZone: BUSINESS_TIME_ZONE,
  };

  const formattedDateTime: string = new Date(dateString).toLocaleString(
    "en-US",
    dateTimeOptions
  );

  const formattedDateDay: string = new Date(dateString).toLocaleString(
    "en-US",
    dateDayOptions
  );

  const formattedDateDayLong: string = new Date(dateString).toLocaleString(
    "en-US",
    dayLongOptions
  );

  const formattedDate: string = new Date(dateString).toLocaleString(
    "en-US",
    dateOptions
  );

  const formattedTime: string = new Date(dateString).toLocaleString(
    "en-US",
    timeOptions
  );

  return {
    dateTime: formattedDateTime,
    dateDay: formattedDateDay,
    dateDayLong: formattedDateDayLong,
    dateOnly: formattedDate,
    timeOnly: formattedTime,
  };
};

export function formatAmount(amount: number): string {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });

  return formatter.format(amount);
}

export const parseStringify = (value: any) => JSON.parse(JSON.stringify(value));

export const removeSpecialCharacters = (value: string) => {
  return value.replace(/[^\w\s]/gi, "");
};

interface UrlQueryParams {
  params: string;
  key: string;
  value: string;
}

export function formUrlQuery({ params, key, value }: UrlQueryParams) {
  const currentUrl = qs.parse(params);

  currentUrl[key] = value;

  return qs.stringifyUrl(
    {
      url: window.location.pathname,
      query: currentUrl,
    },
    { skipNull: true }
  );
}

// Generate an array of years (last 5 years to next 5 years)
export const generateYearRange = () => {
  const currentYear = businessYear();
  return Array.from({ length: 11 }, (_, i) => (currentYear - 5 + i).toString());
};

interface removeKeysParams {
  params: string;
  keysToRemove: string[];
}

export const removeKeysFromQuery = ({
  params,
  keysToRemove,
}: removeKeysParams) => {
  const currentUrl = qs.parse(params);

  keysToRemove.forEach((key) => {
    delete currentUrl[key];
  });

  return qs.stringifyUrl(
    { url: window.location.pathname, query: currentUrl },
    { skipNull: true }
  );
};

export function extractCustomerIdFromUrl(url: string) {
  // Split the URL string by '/'
  const parts = url.split("/");

  // Extract the last part, which represents the customer ID
  const customerId = parts[parts.length - 1];

  return customerId;
}

export function encryptId(id: string) {
  return btoa(id);
}

export function decryptId(id: string) {
  return atob(id);
}

export const getTransactionStatus = (date: Date) => {
  const today = new Date();
  const twoDaysAgo = new Date(today);
  twoDaysAgo.setDate(today.getDate() - 2);

  return date > twoDaysAgo ? "Processing" : "Success";
};

export const authFormSchema = (type: string) =>
  z.object({
    // sign up
    firstName: type === "sign-in" ? z.string().optional() : z.string().min(3),
    lastName: type === "sign-in" ? z.string().optional() : z.string().min(3),
    address1: type === "sign-in" ? z.string().optional() : z.string().max(50),
    city: type === "sign-in" ? z.string().optional() : z.string().max(50),
    state:
      type === "sign-in" ? z.string().optional() : z.string().min(2).max(2),
    postalCode:
      type === "sign-in" ? z.string().optional() : z.string().min(3).max(6),
    dateOfBirth: type === "sign-in" ? z.string().optional() : z.string().min(3),
    ssn: type === "sign-in" ? z.string().optional() : z.string().min(3),
    // both
    email: z.string().email(),
    password: z.string().min(8),
  });

export const getUserBranches = (user: User) => {
  return user.branch_role.map((branch) => branch.branch_id);
};

export const getUserRole = (user: User | undefined): string[] => {
  if (!user || !user.branch_role) return [];
  return user.branch_role.map((branch) => branch.branch_role);
};

// Whether a user may start impersonation. The backend enforces the real rule
// ("principal or higher"); this only gates the UI so we don't offer the control
// to teachers/managers/parents. Backend returns 403 if abused.
export const canImpersonate = (roles: string[] | undefined): boolean => {
  if (!roles) return false;
  const allowed = new Set(["principal", "masteradmin", "superadmin"]);
  return roles.some((role) => allowed.has(role));
};

export const getBranchCountry = (
  id: number,
  branchRole: BranchRole[]
): string => {
  return branchRole.find((br) => br.branch_id === id)!.country;
};
export const camelCase = (word?: string | null) => {
  if (!word) return "";
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export function extractDate(isoTimestamp: string | undefined): string {
  // Split the string at 'T' to separate the date and time
  if (isoTimestamp) {
    const [date] = isoTimestamp.split("T");
    return date;
  }
  return "";
}

export function extractDateTime(isoTimestamp: string | undefined): { date: string; time: string } {
  if (!isoTimestamp) return { date: "", time: "" };

  const [date, timePart] = isoTimestamp.split("T");
  if (!date || !timePart) return { date: "", time: "" };

  // Clean up time string
  const cleanTime = timePart.replace(/Z$/, "").split(".")[0]; // "14:00:00"
  const [hourStr, minuteStr] = cleanTime.split(":");

  let hour = parseInt(hourStr, 10);
  const minutes = minuteStr;

  // Convert to 12-hour format
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12; // 0 → 12

  const formattedTime = `${hour}:${minutes} ${ampm}`;

  return { date, time: formattedTime };
}


export function processCalendarData(data: CalendarData[]): EventInput[] {
  return data.map((event) => {
    const colors = getEventColor(event.entry_type);
    return {
      id: String(event.id),
      title: event.title,
      start: event.start_datetime,
      end: event.end_datetime,
      backgroundColor: colors.backgroundColor,
      borderColor: colors.borderColor,
      textColor: colors.textColor,
      extendedProps: {
        description: event.description,
        venue: (event as any).venue,
        entry_type: event.entry_type,
        branch_id: event.branch_id,
        branch_name: event.branch_name,
        calendarId: event.id,
      },
    };
  });
}

function getEventColor(entryType: string): {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
} {
  switch (entryType.toLowerCase()) {
    case "centre holiday":
      return {
        backgroundColor: "#FDE68A", // Soft yellow
        borderColor: "#F59E0B", // Darker yellow
        textColor: "#92400E", // Dark brown
      };
    case "public holiday":
      return {
        backgroundColor: "#BBF7D0", // Soft green
        borderColor: "#22C55E", // Darker green
        textColor: "#166534", // Dark green
      };
    case "event":
      return {
        backgroundColor: "#BFDBFE", // Soft blue
        borderColor: "#3B82F6", // Darker blue
        textColor: "#1E40AF", // Dark blue
      };
    default:
      return {
        backgroundColor: "#FDA4AF", // Soft pink
        borderColor: "#F43F5E", // Darker pink
        textColor: "#9F1239", // Dark pink
      };
  }
}

export function filterCalendarEvents(calendarData: CalendarData[]) {
  const holidayEvents: CalendarData[] = [];
  const eventList: CalendarData[] = [];
  const otherEvents: CalendarData[] = [];

  calendarData.forEach((event) => {
    switch (event.entry_type.toLowerCase()) {
      case "centre holiday":
      case "public holiday":
        holidayEvents.push(event);
        break;
      case "event":
        eventList.push(event);
        break;
      case "other":
      default:
        otherEvents.push(event);
        break;
    }
  });

  return { holidayEvents, eventList, otherEvents };
}

function groupLessonsByDate(data: LessonData[]): GroupedLesson[] {
  const groupedLessons: Record<string, GroupedLesson> = {};

  data.forEach((lesson) => {
    const dateKey = lesson.lesson_date;

    // Initialize the date entry if it doesn't exist
    if (!groupedLessons[dateKey]) {
      groupedLessons[dateKey] = {
        date: lesson.lesson_date,
        month: lesson.month,
        year: lesson.year,
        day: lesson.day,
        theme_lesson_kids: null,
        theme_lesson_kiddo: null,
        theme_lesson_superkids: null,
      };
    }

    // Assign theme lessons based on category
    switch (lesson.category) {
      case "Kids":
        groupedLessons[dateKey].theme_lesson_kids =
          lesson.theme_lesson.name + " - (" + lesson.theme.name + ")";
        break;
      case "Kiddo":
        groupedLessons[dateKey].theme_lesson_kiddo =
          lesson.theme_lesson.name + " - (" + lesson.theme.name + ")";
        break;
      case "Superkids":
        groupedLessons[dateKey].theme_lesson_superkids =
          lesson.theme_lesson.name + " - (" + lesson.theme.name + ")";
        break;
    }
  });

  // Convert the object to an array and return
  return Object.values(groupedLessons);
}

// Example usage
export function processLessonData(data: LessonData[]): GroupedLesson[] {
  return groupLessonsByDate(data);
}

export function getUserRoleAndUserType(pathname: string): {
  role: string[] | undefined;
  type: string | undefined;
} {
  switch (pathname) {
    case "/users/superadmin":
      return { role: IsSuperadmin, type: SUPERADMIN };
    case "/users/masteradmin":
      return { role: IsMasterAdminOrHigher, type: MASTERADMIN };
    case "/users/principal":
      return { role: IsPrincipalOrHigher, type: PRINCIPAL };
    case "/users/manager":
      return { role: IsManagerOrHigher, type: MANAGER };
    case "/users/teacher":
      return { role: IsManagerOrHigher, type: TEACHER };
    case "/users/parent":
      return { role: IsManagerOrHigher, type: PARENT };
    default:
      return { role: undefined, type: undefined };
  }
}

export const getStatusColor = (status: string) => {
  switch (status) {
    case "ATTENDED":
    case "IN_PROGRESS":
      return "bg-green-100 text-green-800";
    case "GRADUATED":
      return "bg-blue-100 text-blue-800";
    case "DROPPED_OUT":
    case "ABSENT":
      return "bg-red-100 text-red-800";
    case "FREEZED":
      return "bg-orange-100 text-orange-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export type VideoEmbed = {
  /** Undefined when the URL cannot be embedded and we must link out instead. */
  embedUrl?: string;
  watchUrl: string;
  portrait: boolean;
};

// Facebook's video plugin resolves `href` server-side against a canonical
// permalink. Share tokens and fb.watch shortlinks are not permalinks, so the
// plugin renders "Video unavailable" instead of a player.
const FB_UNEMBEDDABLE = /(facebook\.com\/share\/|fb\.watch\/)/i;

export const getVideoEmbed = (url: string | null): VideoEmbed | undefined => {
  if (!url) return undefined;
  const watchUrl = url.trim();
  if (!watchUrl) return undefined;

  try {
    if (watchUrl.includes("youtu.be") || watchUrl.includes("youtube.com")) {
      let videoId = "";
      let portrait = false;

      if (watchUrl.includes("youtu.be/")) {
        videoId = watchUrl.split("youtu.be/")[1] || "";
      } else if (watchUrl.includes("youtube.com/watch")) {
        videoId = new URLSearchParams(new URL(watchUrl).search).get("v") || "";
      } else if (watchUrl.includes("youtube.com/shorts/")) {
        videoId = watchUrl.split("youtube.com/shorts/")[1] || "";
        portrait = true;
      } else if (watchUrl.includes("youtube.com/live/")) {
        videoId = watchUrl.split("youtube.com/live/")[1] || "";
      }

      videoId = videoId.split(/[?&/]/)[0];
      if (videoId) {
        return {
          embedUrl: `https://www.youtube.com/embed/${videoId}`,
          watchUrl,
          portrait,
        };
      }
    }

    if (watchUrl.includes("facebook.com") || watchUrl.includes("fb.watch")) {
      if (FB_UNEMBEDDABLE.test(watchUrl)) return { watchUrl, portrait: true };
      return {
        embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
          watchUrl
        )}&show_text=false`,
        watchUrl,
        portrait: /facebook\.com\/reel\//i.test(watchUrl),
      };
    }

    // Drive serves /view with X-Frame-Options: DENY; /preview is embeddable.
    const driveFile = watchUrl.match(/drive\.google\.com\/file\/d\/([^/?#]+)/i);
    if (driveFile) {
      return {
        embedUrl: `https://drive.google.com/file/d/${driveFile[1]}/preview`,
        watchUrl,
        portrait: false,
      };
    }

    return { embedUrl: watchUrl, watchUrl, portrait: false };
  } catch (error) {
    console.error("Error processing video URL:", error);
    return { watchUrl, portrait: false };
  }
};

export function getCategoryByGrade(grade: number): string {
  switch (grade) {
    case 1:
    case 2:
      return "Kiddo";
    case 3:
    case 4:
      return "Kids";
    case 5:
    case 6:
      return "Superkids";
    default:
      return "Superkids";
  }
}

export function capitalizeFirstLetter(str: string): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function dateIsBeforeToday(date: string): boolean {
  return new Date(date).getTime() < new Date().setHours(0, 0, 0, 0);
}

// Week and month boundaries are derived from the literal "YYYY-MM-DD" fields and
// stepped entirely in UTC. Reading local fields off a Date and then re-projecting
// through toISOString() shifts the day at +08, so the server and the browser
// landed on different weeks -> React hydration #425/#422.
function dateKeyParts(dateString: string): [number, number, number] {
  const key = /^\d{4}-\d{2}-\d{2}/.test(dateString ?? "")
    ? dateString.slice(0, 10)
    : businessToday();
  const [year, month, day] = key.split("-").map(Number);
  // The value comes from the URL, so it can be well-formed and still absurd
  // ("9999-99-99"). Stepping such a date lands outside the range a Date can
  // hold, and every later toISOString() on it throws "Invalid Date".
  if (Number.isNaN(Date.UTC(year, month - 1, day)) || year < 1900 || year > 2200) {
    const [safeYear, safeMonth, safeDay] = businessToday().split("-").map(Number);
    return [safeYear, safeMonth, safeDay];
  }
  return [year, month, day];
}

const utcKey = (date: Date) =>
  Number.isNaN(date.getTime()) ? businessToday() : date.toISOString().slice(0, 10);

export function getMonday(dateString: string): string {
  const [year, month, day] = dateKeyParts(dateString);
  const date = new Date(Date.UTC(year, month - 1, day));
  const weekday = date.getUTCDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
  date.setUTCDate(date.getUTCDate() + (weekday === 0 ? -6 : 1 - weekday));
  return utcKey(date);
}

export function getSunday(dateString: string): string {
  const [year, month, day] = dateKeyParts(dateString);
  const date = new Date(Date.UTC(year, month - 1, day));
  const weekday = date.getUTCDay();
  date.setUTCDate(date.getUTCDate() + (weekday === 0 ? 0 : 7 - weekday));
  return utcKey(date);
}

export function getThisMonth(dateString: string): string {
  const [year, month] = dateKeyParts(dateString);
  return utcKey(new Date(Date.UTC(year, month - 1, 1)));
}

export function getFirstDayBasedOnRole(
  dateString: string,
  isSuperadmin: boolean
): string {
  const [year, month] = dateKeyParts(dateString);
  return utcKey(new Date(Date.UTC(year, month - 1 - (isSuperadmin ? 1 : 0), 1)));
}

export function getFirstDayofLastMonth(
  dateString: string,
): string {
  const [year, month] = dateKeyParts(dateString);
  return utcKey(new Date(Date.UTC(year, month - 2, 1)));
}

// FORMAT DATE FOR CARD VIEWS (GB format: "01 Jan 2025")
export const formatDate = (value?: string | null): string => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
};

// FORMAT TIME FOR CARD VIEWS
export const formatTime = (time?: string): string => {
  if (!time) return "-";
  return time;
};

/**
 * Normalises a name typed into a confirm-to-delete box before comparing it with
 * the name on screen. Stored names carry stray whitespace (trailing spaces from
 * imports, double spaces where a null last_name was joined in), which nobody can
 * reproduce by typing. Case and spacing are not what the confirmation is testing.
 */
export function normalizeConfirmName(value: string | null | undefined) {
  return (value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}
